<template>
  <div>
    <h1 class="spacing-lg-bottom subpage-header">{{ t('sereignundirsida_haeder1') }}</h1>

    <div v-if="sereignRettindi">
      <div v-if="sereignRettindi?.rettindi && sereignRettindi.rettindi.length > 0">
        <v-row class="sereign-cards spacing-lg-bottom" dense>
          <v-col
            v-for="(item, index) in sereignInfo"
            :key="index"
            cols="12"
            md="4"
            class="d-flex"
          >
            <BaseCard class="sereign-card flex-grow-1">
              <template v-if="cardLoading">
                <v-skeleton-loader type="heading, text, text" />
              </template>

              <template v-else>
                <div class="box-title">
                  {{ t(item.title) }}
                </div>

                <div v-if="item.currency" class="box-amount">
                  <span class="amount">{{ currency(item.info) }}</span>
                  <span class="unit">kr.</span>
                </div>

                <div
                  v-else
                  class="box-amount box-amount--text"
                  :class="{ 'box-amount--stacked': toLines(item.info).length > 1 }"
                >
                  <span v-for="(line, i) in toLines(item.info)" :key="i" class="amount">{{ line }}</span>
                </div>
              </template>
            </BaseCard>
          </v-col>
        </v-row>

        <h2>{{ t('sereignTable_hreyfingar') }}</h2>

        <!-- Picker row: Year + From/To + Button (inline) -->
        <div class="picker-row">
          <v-select
            v-model="selectedYear"
            :items="yearOptions"
            :label="labelYear"
            variant="outlined"
            density="compact"
            hide-details
            class="picker-field year-select"
          />

          <!-- Your DatePicker (DD.MM.YYYY) -->

          <DatePicker
            v-model="dagsFraInput"
            :label="labelFrom"
            density="compact"
            variant="outlined"
            :hide-details="true"
          />
          <DatePicker
            v-model="dagsTilInput"
            :label="labelTo"
            density="compact"
            variant="outlined"
            :hide-details="true"
          />

          <!-- custom button -->
          <BaseButton variant="primary" @click="applyDateFilter">
            {{ t('gogntiltr_umsokn_afram') }}
          </BaseButton>

          <div v-if="loading" class="picker-loading">
            <v-progress-circular indeterminate size="32" color="teal-lighten-1" />
          </div>
        </div>

        <v-row>
          <v-col v-if="!loading" md="12">
            <v-tabs
              v-if="tabswithData.length > 0"
              v-model="model"
              class="subpage__tabs"
              bg-color="transparent"
              grow
              color="#00877C"
            >
              <v-tab
                v-for="(item, i) in tabswithData"
                :key="i"
                :value="`tab-${i}`"
              >
                {{ item.sjodsDeild }}
              </v-tab>
            </v-tabs>

            <v-window v-if="tabswithData.length > 0" v-model="model">
              <v-window-item
                v-for="(item, i) in tabswithData"
                :key="i"
                :value="`tab-${i}`"
              >
                <PrivatePensionTable
                  v-if="hreyfingarResults.length > 0"
                  :ref="'sereignTable-' + i"
                  :rettindi-deilda="item.sjodsDeildNr"
                  :chosen-year="chosenDate"
                  :dags-fra="dagsFra"
                  :dags-til="dagsTil"
                  :loading="loading"
                  :sjods-deild="item.sjodsDeild"
                  :SereignHreyfingar="hreyfingarResults[i].timabil"
                  class="subpage__table subpage__table-default"
                />
              </v-window-item>
            </v-window>

            <div v-else class="nodata-msg">
              <h5>{{ t('sereign_no_data') }}</h5>
            </div>
          </v-col>
        </v-row>
      </div>
    </div>

    <!-- ===== Idgjöld (Séreign) ===== -->
    <h2 class="mt-6">{{ t('subpage_idgjold_header') }}</h2>
    <IdgjaldaTable
      :idgjold="filteredIdgjold"
      class="subpage__table subpage__table-default"
    />

    <v-container>
      <v-row v-if="sereignRettindi">
        <h2 v-if="sereignRettindi.rettindi && sereignRettindi.rettindi.length === 0">
          {{ t('private_pensions_no_balance') }}
        </h2>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import moment from 'moment'

import PrivatePensionTable from '@/components/privatePension/privatePensionTable.vue'
import IdgjaldaTable from '@/components/idgjald/IdgjaldTableFull.vue'
import { useJoakimStore } from '@/stores/useJoakimStore'
import { storeToRefs } from 'pinia'
import BaseCard from '@/components/shared/BaseCard.vue'
import BaseButton from '@/components/customButton/CustomButton.vue'
import DatePicker from '@/components/Applications/Shared/DatePicker.vue'
import { useCurrency } from '@/composables/useCurrency'

const { currency } = useCurrency()
const { t } = useI18n()
const store = useJoakimStore()
// Define the expected type for sereignRettindi
interface RettindiDeildaType {
  sjodsDeild?: string;
  sjodsDeildNr?: string | number;
  [key: string]: any;
}
interface RettindiType {
  rettindiDeilda?: RettindiDeildaType[];
  [key: string]: any;
}
interface SereignRettindiType {
  rettindi?: RettindiType[];
  rettindiDeild?: RettindiDeildaType[];
  rettindiDeilda?: RettindiDeildaType[];
  [key: string]: any;
}

const { sereignRettindi, sereign, tilgreindSereign } = storeToRefs(store) as {
  sereignRettindi: Ref<SereignRettindiType | null>;
  sereign: Ref<any>;
  tilgreindSereign: Ref<any>;
}
const { getSereign, getIdgjold, setSereign } = store

/* ---------------- state ---------------- */
const model = ref('tab-0')

const Idgjold = ref<any[] | null>(null)
const sereignInfo = ref<any[]>([])
const rettindiDeildaCombined = ref<any[]>([])
const tabswithData = ref<any[]>([])
const hreyfingarResults = ref<any[]>([])

const loading = ref(false)
const initialLoading = ref(true)
const ready = ref(false) // watchers blocked until true

let debounceTimer: ReturnType<typeof setTimeout> | null = null
let fetchToken = 0 // concurrency guard

/* ---------------- labels ---------------- */
function tt (...keys: string[]): string {
  for (const k of keys) {
    const s = t(k as any)
    if (s && s !== k) return String(s)
  }
  return keys[keys.length - 1]
}
const labelYear = computed(() => tt('general_choose_year','message.general_choose_year','year','Ár'))
const labelFrom = computed(() => tt('lifeyri_umsokn_step4_datefrom','general_from','from','Frá'))
const labelTo   = computed(() => tt('ororku_umsokn_step9_healthorganizations_to','general_to','to','Til'))

/* ---------------- card skeleton ---------------- */
const cardLoading = computed(() => {
  const totalsReady =
    Number.isFinite(Number(sereign.value)) &&
    Number.isFinite(Number(tilgreindSereign.value))
  return initialLoading.value || !totalsReady || sereignInfo.value.length === 0
})

/* ---------------- dates ---------------- */
const dagsTil = ref<Date>(new Date())
const dagsFra = ref<Date>(moment(dagsTil.value).subtract(1, 'years').add(1, 'months').startOf('month').toDate())
const chosenDate = ref<Date>(new Date())

const selectedYear = ref<number>(new Date().getFullYear())
const yearOptions = computed<number[]>(() => {
  const now = new Date().getFullYear()
  return Array.from({ length: 7 }, (_, i) => now - i)
})

/* DatePicker strings (DD.MM.YYYY) */
const dagsFraInput = ref<string>(formatDateInput(dagsFra.value))
const dagsTilInput = ref<string>(formatDateInput(dagsTil.value))

/* ---------------- lifecycle ---------------- */
onMounted(async () => {
  try {
    initialLoading.value = true

    if (sereign.value == null || tilgreindSereign.value == null) {
      await setSereign()
    }
    await getSereign()
    Idgjold.value = await getIdgjold()

    syncDateInputs()
    setSereignInfo()

    ready.value = true
    await refreshHreyfingar() // single initial fetch
  } finally {
    initialLoading.value = false
  }
})

/* ---------------- watchers (debounced) ---------------- */
watch(() => sereignRettindi.value, () => {
  if (!ready.value) return
  setSereignInfo()
  setDatesForCurrentYear()
  selectedYear.value = new Date().getFullYear()
  syncDateInputs()
  scheduleRefresh()
})

watch(() => sereign.value, () => {
  if (!ready.value) return
  setSereignInfo()
})

watch(tabswithData, (arr) => {
  if (arr && arr.length > 0) model.value = 'tab-0'
})

watch(selectedYear, (yr) => {
  if (!ready.value) return
  setFromToByYear(yr)
})

watch([dagsFraInput, dagsTilInput], ([f, t]) => {
  if (!ready.value) return
  const df = parseDateInput(f as string)
  const dt = parseDateInput(t as string)
  if (df && dt) {
    dagsFra.value = df
    dagsTil.value = dt
    scheduleRefresh()
  }
})

/* ---------------- actions ---------------- */
function scheduleRefresh(delay = 150) {
  if (!ready.value) return
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    debounceTimer = null
    refreshHreyfingar()
  }, delay)
}

function setFromToByYear(yr: number) {
  const currentYear = new Date().getFullYear()
  if (yr === currentYear) {
    setDatesForCurrentYear()
  } else {
    dagsFra.value = new Date(yr, 0, 1)
    dagsTil.value = new Date(yr, 11, 31)
  }
  syncDateInputs()
  scheduleRefresh()
}

function setDatesForCurrentYear() {
  dagsTil.value = new Date()
  dagsFra.value = moment(dagsTil.value).subtract(1, 'years').add(1, 'months').startOf('month').toDate()
}

function syncDateInputs() {
  dagsFraInput.value = formatDateInput(dagsFra.value)
  dagsTilInput.value = formatDateInput(dagsTil.value)
}

function formatDateInput(d: Date) { return moment(d).format('DD.MM.YYYY') }

function parseDateInput(str: string): Date | null {
  if (!str) return null
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(str)
  if (m) {
    const [, dd, mm, yyyy] = m
    const d = new Date(+yyyy, +mm - 1, +dd)
    return isNaN(d.getTime()) ? null : d
  }
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str)
  if (iso) {
    const [, yyyy, mm, dd] = iso
    const d = new Date(+yyyy, +mm - 1, +dd)
    return isNaN(d.getTime()) ? null : d
  }
  const d = new Date(str)
  return isNaN(d.getTime()) ? null : d
}

function applyDateFilter() {
  if (dagsFra.value > dagsTil.value) {
    const tmp = dagsFra.value
    dagsFra.value = dagsTil.value
    dagsTil.value = tmp
    syncDateInputs()
  }
  chosenDate.value = new Date(dagsTil.value)
  scheduleRefresh()
}

/* ---------------- helpers ---------------- */
function toLines(v: any): string[] { return Array.isArray(v) ? v : [String(v)] }
function positiveAmount(v: any) { const n = Number(v); return Number.isFinite(n) && n > 0 }
function pickAmount(o: any) { return o?.inneign ?? o?.rettindi ?? o?.balance ?? o?.eign ?? 0 }
function uniqueBy<T>(arr: T[], key: (x: T) => any) {
  const seen = new Set<any>()
  return arr.filter((it) => {
    const k = key(it)
    if (k == null || seen.has(k)) return false
    seen.add(k)
    return true
  })
}

function setSereignInfo() {
  combineRettindiDeilda()
  const deildir = Array.from(new Set(
    rettindiDeildaCombined.value
      .filter((d) => positiveAmount(pickAmount(d)))
      .map((d) => d?.sjodsDeild)
      .filter(Boolean)
  ))
  const toNumber = (v: any) => { const n = Number(v); return Number.isFinite(n) ? n : 0 }
  sereignInfo.value = [
    { title: 'sereign_inneign',           info: toNumber(sereign.value ?? 0),          currency: true },
    { title: 'sereign_fjarfestingaleid',  info: deildir.length ? deildir : ['-'],      currency: false },
    { title: 'sereign_tilgreind_inneign', info: toNumber(tilgreindSereign.value ?? 0), currency: true }
  ]
}

function combineRettindiDeilda() {
  const nested = (sereignRettindi.value?.rettindi ?? []).flatMap((r: any) => r?.rettindiDeilda ?? [])
  const top = sereignRettindi.value?.rettindiDeild ?? sereignRettindi.value?.rettindiDeilda ?? []
  const merged = [...nested, ...top].filter(Boolean)
  const uniq = uniqueBy(merged, (d: any) => d?.sjodsDeildNr)
  rettindiDeildaCombined.value = uniq.filter((d: any) => positiveAmount(pickAmount(d)))
}

/* ---------------- API (concurrency-safe) ---------------- */
async function refreshHreyfingar() {
  const myToken = ++fetchToken
  loading.value = true

  if (!rettindiDeildaCombined.value?.length) combineRettindiDeilda()
  if (!rettindiDeildaCombined.value?.length) {
    hreyfingarResults.value = []
    tabswithData.value = []
    loading.value = false
    return
  }

  const from = new Date(dagsFra.value)
  const to = new Date(dagsTil.value)

  try {
    const local: any[] = []
    await Promise.all(
      rettindiDeildaCombined.value.map(async (item: any) => {
        const res = await store.getHreyfingarDeilda({
          sjodsDeild: item.sjodsDeildNr,
          dagsFra: from,
          dagsTil: to
        })
        if (res?.hreyfingar?.length > 0) {
          local.push({
            timabil: res,
            sjodsDeild: item.sjodsDeild,
            sjodsDeildNr: item.sjodsDeildNr
          })
        }
      })
    )

    // stale response? bail
    if (myToken !== fetchToken) return

    // tabs in first-seen order
    const ordered: any[] = []
    const seen = new Set<any>()
    for (const r of local) {
      if (!seen.has(r.sjodsDeildNr)) {
        seen.add(r.sjodsDeildNr)
        ordered.push({ sjodsDeild: r.sjodsDeild, sjodsDeildNr: r.sjodsDeildNr })
      }
    }

    hreyfingarResults.value = local
    tabswithData.value = ordered
    if (ordered.length > 0) model.value = 'tab-0'
  } finally {
    if (myToken === fetchToken) loading.value = false
  }
}

/* ---------------- filters ---------------- */
const SEREIGN_FILTER: Set<string> = new Set([
  'X203T','X202T','X201T','X201','X201B','X201L','X202','X202B','X202L','X203','X203B','X203L','X201U','X202U','X203U'
])
const filteredIdgjold = computed(() => {
  const rows = Array.isArray(Idgjold.value) ? Idgjold.value
    : Array.isArray((Idgjold.value as any)?.idgjold) ? (Idgjold.value as any).idgjold : []
  return rows.filter((x: any) => Number(x.samtals) >= 0 && SEREIGN_FILTER.has(String(x.deildNr)))
})
</script>


<style lang="scss" scoped>
@use "@/scss/settings/variables" as var;
@use "@/scss/settings/mixins" as mixins;

/* Cards */
.sereign-cards { row-gap: 16px; }
.sereign-card { min-height: 140px; display: flex; flex-direction: column; justify-content: center; }

/* Titles & amounts */
.box-title {
  font-family: var.$font-family-base !important;
  font-size: var.$font-size-base;
  font-weight: var.$font-weight-medium !important;
  color: var.$color-text;
  margin-bottom: 16px;
  @include mixins.text-heading;
  :global(body.dark-mode) & { color: var.$color-text-dark; }
}
.box-amount {
  display: flex; align-items: baseline; gap: 4px;
  font-size: 2.2rem; font-weight: var.$font-weight-medium; color: var.$color-text; letter-spacing: 1px;
  @include mixins.text-heading;
  :global(body.dark-mode) & { color: #fff; }
  .amount { line-height: 1; }
  .unit { font-size: 1.1rem; font-weight: var.$font-weight-regular; color: var.$color-text; margin-left: 4px;
    :global(body.dark-mode) & { color: var.$color-text-dark; } }

  &.box-amount--text {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;

    .unit { display: none; }

    &.box-amount--stacked {
      font-size: 1.1rem;
      letter-spacing: normal;
      .amount { line-height: 1.35; }
    }
  }
}

/* --- keep inline & bottoms aligned --- */
.picker-row {
  display: flex;
  align-items: flex-end; /* bottom-align row */
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

/* Shared width */
.picker-field { width: 180px; }
.year-select  { width: 140px; }

/* Unify control heights (match Vuetify compact outlined) */
:root { --picker-h: 40px; } /* adjust to your design if needed */

.year-select :deep(.v-field),
.date-select :deep(.v-field) {
  min-height: var(--picker-h);
}

/* The v-menu wrapper around DatePicker can sit on baseline → pin it to bottom */
.date-select { align-self: flex-end; }

/* Make the Apply button the same height & baseline align */
.picker-apply {
  align-self: flex-end;
  height: var(--picker-h);
  display: inline-flex;
  align-items: center;
  padding: 0 16px; /* keep your button’s look */
}

/* Loader stays centered vertically next to controls */
.picker-loading { display: flex; align-items: center; }


/* Mobile */
@media (max-width: 720px) {
  .picker-field, .year-select { width: 100%; }
  .picker-apply { width: 100%; }
}
.nodata-msg { margin-bottom: 20px; }
</style>
